import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useContext, useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import ConnectedBottomDeals, {
  LAYOUT_TYPES,
  template,
} from '../../../../components/deal/ConnectedBottomDeals';
import {
  fetchDeals,
  specialGetKeyGenerator,
} from '../../../../components/deal/ConnectedBottomDeals/helpers';
import TrustBox from '../../../../components/deal/TrustBox';
import Tiles from '../../../../components/tiles/Tiles';
import { SEARCH_PAGE_SIZE } from '../../../../config/links/links';
import { BRAND_WOWCHER } from '../../../../config/setup/setup';
import { TITLE_INDEX } from '../../../../config/text/text';
import { trackPage } from '../../../../helpers/analytics';
import { getSpecialApiPath } from '../../../../helpers/api';
import { useIsSSR } from '../../../../helpers/ssr';
import { slugAsTitle } from '../../../../helpers/url';
import ThemeContext from '../../../../providers/ThemeProvider';
import { setOnlySSR } from '../../../../redux/actions/deals';
import { getServerSideEssentials } from '../../../../redux/actions/ssr';
import { getTiles } from '../../../../redux/actions/tiles';
import { wrapper } from '../../../../redux/store/store';

const SpecialPage = ({ title, errorCode, deals }) => {
  const theme = useContext(ThemeContext);
  const dispatch = useDispatch();
  const isSSR = useIsSSR();
  const {
    asPath,
    query: { location, slug },
  } = useRouter();
  const tiles = useSelector((state) => state.tiles);

  useEffect(() => {
    dispatch(getTiles(asPath, tiles.apiUrl, tiles.tiles?.length || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asPath]);

  useEffect(() => {
    if (!errorCode) {
      trackPage({ location: { shortName: location } });
    }
  }, [errorCode, location, dispatch]);

  if (errorCode) {
    const ErrorPage = dynamic(() =>
      import('../../../../components/errors/Form404'),
    );

    return <ErrorPage statusCode={404} />;
  }

  return (
    <>
      <Head>
        <title>{title}</title>
      </Head>
      <TrustBox />
      <div className="rel-container">
        <div className="container">
          <Tiles />
          <ConnectedBottomDeals
            fetcherFunction={fetchDeals}
            getKeyGenerator={specialGetKeyGenerator({ location, slug })}
            pageSize={SEARCH_PAGE_SIZE}
            templateType={LAYOUT_TYPES.twoByTwo}
          />
          {isSSR &&
            template({
              deals,
              path: asPath,
              theme,
              type: LAYOUT_TYPES.twoByTwo,
            })}
        </div>
      </div>
      <style jsx>{`
        .rel-container {
          position: relative;
        }
        .container {
          margin-top: 0;
          padding: 0;
        }
        @media (min-width: ${theme.breakpoints.smUp}) {
          .container {
            margin-top: 20px;
            margin-bottom: 20px;
            padding-left: 15px;
            padding-right: 15px;
          }
        }
      `}</style>
    </>
  );
};

SpecialPage.propTypes = {
  deals: PropTypes.arrayOf(PropTypes.object),
  errorCode: PropTypes.bool,
  title: PropTypes.string,
};

export const getServerSideProps = wrapper.getServerSideProps(
  async (context) => {
    const { req, store, query } = context;
    await store.dispatch(getServerSideEssentials(req.url)).catch((error) => {
      console.log(`getServerSideEssentials error: ${error}`);
    });
    store.dispatch(setOnlySSR(false));

    try {
      const slug = query.slug;
      const location = query.location;
      const url = getSpecialApiPath({ location, slug }).toString();
      const deals = await fetchDeals(url);

      const title = `${slugAsTitle(slug)} | ${
        TITLE_INDEX[process.env.NEXT_PUBLIC_BRAND || BRAND_WOWCHER]
      }`;

      if (!req.url.includes('/_next/') && deals.length === 0) {
        return {
          notFound: true,
        };
      }

      return { props: { deals, errorCode: deals.length === 0, title } };
    } catch (error) {
      console.log(`getSpecialDeals error: ${error}`);

      return { props: { errorCode: true } };
    }
  },
);

export default connect()(SpecialPage);
